import { useObservableState } from 'observable-hooks';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import Form from '../../../components/form';
import Checkbox from '../../../components/form/elements/checkbox';
import Group from '../../../components/form/elements/group';
import Link from '../../../components/form/elements/link';
import Decimal from '../../../components/form/elements/decimal';
import Readonly from '../../../components/form/elements/readonly';
import Show from '../../../components/form/elements/show';
import Text from '../../../components/form/elements/text';
import TextArea from '../../../components/form/elements/text-area';
import { Product } from '../../../interfaces/product';
import {
  WAREHOUSE_PRODUCTS_COPY,
  WAREHOUSE_PRODUCTS_EDIT,
  WAREHOUSE_PRODUCTS_INDEX,
} from '../../../router/routes';
import { useService } from '../../../services/services';
import { can, numberTransform, route } from '../../../services/utils';
import { ProductObtainType } from '../../../enums/product-obtain-type';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';

export const productSchema = yup.object().shape({
  category_id: yup.object().shape({
    value: yup.string().required().uuid(),
  }),
  type_id: yup.object().shape({
    value: yup.string().required().uuid(),
  }),
  name: yup
    .string()
    .max(255)
    .required()
    .when('category_id', (category: any) => {
      if (
        category?.label === 'Augu aizsardzības līdzekļi' ||
        category?.label === 'Mēslojums'
      ) {
        return yup.object().shape({
          label: yup.string().required(),
        });
      }
    }),
  unit: yup.object().shape({
    value: yup.string().required(),
  }),
  planned_price: yup
    .number()
    .positive()
    .decimal()
    .nullable()
    .transform(numberTransform),
  article: yup.string().max(255).nullable(),
  active: yup.boolean(),
  comment: yup.string().nullable(),
});

export default function ProductsView() {
  const { farmId, id, obtainTypeId } = useParams<{
    farmId: string;
    id: string;
    obtainTypeId: ProductObtainType;
  }>();
  const { t } = useTranslation();
  const { productService } = useService();
  const [bodyElements, setBodyElements] = useState([]);
  const [footerElements, setFooterElements] = useState([]);

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const [product] = useObservableState<Product>(
    () => productService.show(farmId, id),
    null
  );

  useEffect(() => {
    if (product === null) {
      return;
    }

    setBodyElements([
      Readonly([
        Group([
          Text('obtain_type.name', t('pages.warehouse.products.type')),
          Text(
            'category.value',
            t('pages.warehouse.products.product_category')
          ),
          Text('type.value', t('pages.warehouse.products.product_type')),
          Text('name', t('pages.warehouse.products.name')),
          Text('unit', t('pages.warehouse.products.unit')),
          Show(
            [
              Decimal('planned_price', t('pages.warehouse.products.price')),
              Text('article', t('pages.warehouse.products.article')),
            ],
            '',
            () =>
              obtainTypeId === ProductObtainType.PRODUCED ||
              obtainTypeId === ProductObtainType.RECYCLE_GAINED
          ),
          Checkbox('active', t('pages.warehouse.products.isActual')),
          TextArea('comment', t('pages.warehouse.products.comment')),
        ]),
      ]),
    ]);

    setFooterElements(() => {
      let elements = [];

      if (can(farmUser, { title: 'update product' })) {
        elements.push(
          Link(
            route(WAREHOUSE_PRODUCTS_EDIT, { farmId, obtainTypeId, id }),
            'edit',
            true
          )
        );
      }

      if (can(farmUser, { title: 'create product' })) {
        elements.push(
          Link(
            route(WAREHOUSE_PRODUCTS_COPY, { farmId, obtainTypeId, id }),
            'copy',
            true
          )
        );
      }

      return elements;
    });
  }, [
    farmUser,
    product,
    farmId,
    id,
    obtainTypeId,
    productService,
    t,
    setBodyElements,
    setFooterElements,
  ]);

  const formTitle = product
    ? product.name
    : t('pages.warehouse.products.view-product');

  return (
    <Form
      mode={'view'}
      title={formTitle}
      backTo={route(WAREHOUSE_PRODUCTS_INDEX, { farmId, obtainTypeId })}
      body={bodyElements}
      footer={footerElements}
      values={product}
      submit={null}
      schema={productSchema}
      noticeAccessor="ViewForm"
    />
  );
}
